import { Link } from "react-router-dom";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/ui/button";
import { FileCheck2, Microscope, Award, ShieldCheck, Boxes, ScrollText, ArrowRight } from "lucide-react";

const PILLARS = [
  { i: FileCheck2, t: "Certificates of Analysis", d: "Every batch ships with a full analytical report — HPLC purity, Mass Spec identity, and appearance." },
  { i: Microscope, t: "Batch Testing", d: "USP <85> LAL endotoxin, sterility, and bioburden checks across the fill line." },
  { i: Award, t: "Manufacturing Standards", d: "USA-based synthesis partners audited annually against documented QMS controls." },
  { i: ShieldCheck, t: "Purity Floor", d: "We publish a strict ≥ 98% floor. Most batches exceed 99%." },
  { i: Boxes, t: "Batch Tracking", d: "A scannable batch ID on every vial ties directly to its full test panel and shipping record." },
  { i: ScrollText, t: "Documentation Library", d: "COAs and MSDS accessible in your customer portal for every historical batch." },
];

export default function Quality() {
  return (
    <div data-testid="quality-page">
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="font-mono text-[11px] uppercase tracking-widest text-blue-400 mb-4">Quality · Documentation</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-4xl">
            Documentation you can <span className="text-blue-400">audit.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">
            Six pillars of quality control. Nothing ships without full analytical verification.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PILLARS.map((p) => (
            <div key={p.t} className="border border-slate-200 rounded-md p-8 bg-white card-clinical">
              <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
                <p.i className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
              </div>
              <div className="mt-6 font-display text-xl font-semibold text-slate-900">{p.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <SectionHeader eyebrow="Process" title="From synthesis to bench." description="A traceable chain of custody at every stage." />
            <div className="space-y-6">
              {[
                { n: "01", t: "Synthesis", d: "USA-based synthesis partner. Lot-level lineage recorded." },
                { n: "02", t: "Independent Assay", d: "Third-party HPLC + Mass Spec. Retention samples archived 24 months." },
                { n: "03", t: "Endotoxin & Sterility", d: "LAL testing per USP <85>. Bioburden monitored across fill." },
                { n: "04", t: "Batch Documentation", d: "COA generated and attached to the batch record." },
                { n: "05", t: "Cold-chain Shipping", d: "Insulated, temperature-controlled to your facility." },
              ].map((s) => (
                <div key={s.n} className="grid grid-cols-[56px_1fr] gap-5 border-b border-slate-200 pb-5 last:border-b-0">
                  <div className="font-mono text-blue-700 text-sm">{s.n}</div>
                  <div>
                    <div className="font-display text-lg font-semibold text-slate-900">{s.t}</div>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 border border-slate-200 rounded-md p-8 bg-white">
            <div className="eyebrow mb-3">Sample COA · P4P-2026-042</div>
            <div className="font-display text-2xl font-semibold text-slate-900">BPC-157</div>
            <div className="text-xs text-slate-500 mt-1">Body Protective Compound-157</div>
            <div className="mt-6 divide-y divide-slate-100 border border-slate-200 rounded-md">
              {[
                ["Purity (HPLC)", "99.24%"],
                ["Identity (MS)", "Confirmed"],
                ["Endotoxin", "< 0.05 EU/mg"],
                ["Sterility", "Pass"],
                ["Appearance", "White lyophilized"],
                ["Water content", "2.1%"],
              ].map((r) => (
                <div key={r[0]} className="grid grid-cols-2 px-4 py-2.5">
                  <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{r[0]}</div>
                  <div className="text-sm text-slate-900 font-mono text-right">{r[1]}</div>
                </div>
              ))}
            </div>
            <Link to="/catalog" className="inline-block mt-6">
              <Button className="h-11 px-5 bg-slate-900 hover:bg-blue-600 text-white rounded-md transition-colors">
                Browse compounds <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="eyebrow mb-4 justify-center">Statement of Use</div>
        <p className="text-slate-700 text-lg leading-relaxed max-w-3xl mx-auto">
          All Peps4Pets content is intended for licensed veterinary and research professionals only. Nothing on
          this site constitutes medical advice or a prescription. Compounds are not FDA-approved and are not
          intended for human consumption.
        </p>
      </section>
    </div>
  );
}
